import React from 'react';
import {
  Box, Typography, Button, Card, CardContent, Alert,
  LinearProgress, Chip, Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

const VerificationStatusStep = ({ state }) => {
  const navigate = useNavigate();

  const getStatusConfig = () => {
    switch (state.verification_status) {
      case 'APPROVED':
        return {
          icon: <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />,
          title: 'Verification Approved!',
          subtitle: 'Your profile has been verified and is now live',
          color: 'success',
          message: 'Congratulations! Your doctor profile has been approved. You can now accept patients and start your practice.',
          actions: [
            {
              label: 'Go to Dashboard',
              variant: 'contained',
              onClick: () => navigate('/doctor-dashboard'),
              icon: <DashboardIcon />,
            },
            {
              label: 'View Profile',
              variant: 'outlined',
              onClick: () => navigate('/doctor-profile'),
            },
          ],
        };
      
      case 'PENDING':
        return {
          icon: <PendingIcon color="warning" sx={{ fontSize: 48 }} />,
          title: 'Verification in Progress',
          subtitle: 'Your documents are under review',
          color: 'warning',
          message: 'We are currently reviewing your submitted documents. This process typically takes 2-3 business days. You will receive an email notification once the review is complete.',
          actions: [
            {
              label: 'Go to Dashboard',
              variant: 'contained',
              onClick: () => navigate('/doctor-dashboard'),
              icon: <DashboardIcon />,
            },
            {
              label: 'Contact Support',
              variant: 'outlined',
              onClick: () => window.open('mailto:support@mindwell.com', '_blank'),
            },
          ],
        };
      
      case 'REJECTED':
        return {
          icon: <ErrorIcon color="error" sx={{ fontSize: 48 }} />,
          title: 'Verification Rejected',
          subtitle: 'Please review and resubmit your documents',
          color: 'error',
          message: 'Your verification request was not approved. Please review the feedback below and resubmit your documents with the necessary corrections.',
          actions: [
            {
              label: 'Resubmit Documents',
              variant: 'contained',
              onClick: () => navigate('/doctor-onboarding'),
              icon: <AssignmentIcon />,
            },
            {
              label: 'Contact Support',
              variant: 'outlined',
              onClick: () => window.open('mailto:support@mindwell.com', '_blank'),
            },
          ],
        };
      
      default:
        return {
          icon: <PendingIcon color="info" sx={{ fontSize: 48 }} />,
          title: 'Verification Status Unknown',
          subtitle: 'Please contact support',
          color: 'info',
          message: 'Unable to determine your verification status. Please contact our support team for assistance.',
          actions: [
            {
              label: 'Contact Support',
              variant: 'contained',
              onClick: () => window.open('mailto:support@mindwell.com', '_blank'),
            },
          ],
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Onboarding Complete!
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Your profile setup is finished. Here's your verification status:
      </Typography>

      {/* Status Card */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            {statusConfig.icon}
            <Box ml={2}>
              <Typography variant="h4" component="h3" gutterBottom>
                {statusConfig.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {statusConfig.subtitle}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" paragraph>
            {statusConfig.message}
          </Typography>

          {/* Status Indicators */}
          <Box display="flex" gap={2} mb={3}>
            <Chip
              label={`Profile: ${state.can_publish_profile ? 'Live' : 'Pending'}`}
              color={state.can_publish_profile ? 'success' : 'warning'}
              variant="outlined"
            />
            <Chip
              label={`Appointments: ${state.can_take_appointments ? 'Enabled' : 'Disabled'}`}
              color={state.can_take_appointments ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>

          {/* Progress for PENDING status */}
          {state.verification_status === 'PENDING' && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Review Progress
              </Typography>
              <LinearProgress 
                variant="indeterminate" 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Estimated completion: 2-3 business days
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* What's Next */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            What's Next?
          </Typography>
          
          {state.verification_status === 'APPROVED' ? (
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Your profile is now visible to patients
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Start accepting appointment bookings
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Access your dashboard to manage patients and appointments
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Set up your consultation fees and payment methods
              </Typography>
            </Box>
          ) : state.verification_status === 'PENDING' ? (
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Wait for verification review (2-3 business days)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Check your email for status updates
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Explore the dashboard while waiting
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Contact support if you have questions
              </Typography>
            </Box>
          ) : (
            <Box component="ul" sx={{ pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Review the feedback provided
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Resubmit corrected documents
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" mb={1}>
                Contact support for clarification if needed
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Support Information */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Need Help?</strong> Our support team is here to assist you.
          Email us at support@mindwell.com or call us at +91-XXXXXXXXXX
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Box display="flex" gap={2} justifyContent="center">
        {statusConfig.actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant}
            size="large"
            onClick={action.onClick}
            startIcon={action.icon}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default VerificationStatusStep;
